import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Collapse, Stack, Typography, Badge } from "@mui/material";
import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router";

export interface SidebarItemType {
  name: string;
  path?: string;
  icon?: React.ReactNode;
  badge?: number;
  subItems?: SidebarItemType[];
  locked?: boolean;
  external?: boolean;
}

export const SidebarItem = ({
  item,
  depth = 0,
}: {
  item: SidebarItemType;
  depth?: number;
}) => {
  const location = useLocation();
  const hasChildren = Array.isArray(item.subItems) && item.subItems.length > 0;

  const isActive = useMemo(() => {
    if (item.path && location.pathname === item.path) {
      return true;
    }
    if (hasChildren && item.subItems) {
      return item.subItems.some((subItem) => {
        if (subItem.path && location.pathname === subItem.path) {
          return true;
        }
        if (subItem.subItems) {
          return subItem.subItems.some(
            (nested) => nested.path && location.pathname === nested.path
          );
        }
        return false;
      });
    }
    return false;
  }, [hasChildren, item.path, item.subItems, location.pathname]);

  const [open, setOpen] = useState(true); // Always open accordions by default

  // Remove the useEffect since we want accordions to stay open

  const defaultColor = !item.path && hasChildren && depth === 0 ? "#B9ACE9" : "#fff";
  // Only change text color for direct path items, not parent items when children are active
  const textColor = isActive && item.path ? "#fff" : defaultColor;
  // Only show background for direct path items, not parent items when children are active
  const background = isActive && item.path ? "#41536F" : "transparent";
  const isExternalLink = Boolean(
    item.path && item.external && !item.locked && !hasChildren
  );
  const isInternalLink = Boolean(
    item.path && !item.external && !item.locked && !hasChildren
  );
  const isClickable = isExternalLink || isInternalLink;
  
  // Handle locked items styling
  const lockedTextColor = item.locked ? "rgba(255, 255, 255, 0.5)" : textColor;
  const lockedIconColor = item.locked ? "rgba(255, 255, 255, 0.5)" : textColor;

  return (
    <>
      <Stack
        component={isInternalLink ? Link : isExternalLink ? "a" : "div"}
        {...(isInternalLink ? { to: item.path } : {})}
        {...(isExternalLink ? { href: item.path } : {})}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 1.5, // Reduced horizontal padding
          py: 0.75, // Reduced vertical padding for tighter spacing
          pl: `${8 + depth * 12}px`, // Reduced indentation
          cursor: hasChildren || isClickable ? "pointer" : "default",
          backgroundColor: background,
          borderRadius: "6px", // More subtle border radius
          minHeight: "36px", // Consistent height
          transition: "all 0.2s ease-in-out", // Smooth transitions
          textDecoration: "none",
          "&:hover": {
            backgroundColor: "#41536F",
            transform: "translateX(2px)", // Subtle hover animation
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)", // Added shadow on hover
          },
        }}
        onClick={() => (hasChildren ? setOpen(!open) : null)}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {item.icon &&
            typeof item.icon === "object" &&
            "type" in item.icon &&
            typeof item.icon.type === "function"
            ? (
                <item.icon.type
                  {...item.icon.props}
                  sx={{ 
                    ...item.icon.props?.sx, 
                    color: lockedIconColor,
                    fontSize: '16px !important', // Smaller icons to match 13px text
                    width: '16px !important',
                    height: '16px !important'
                  }}
                />
              )
            : (
                item.icon
              )}

          <Typography 
            variant="body2" 
            sx={{ 
              color: lockedTextColor, 
              fontSize: '13px !important', // Consistent with link text
              fontWeight: '500 !important', // Medium weight for better readability
              lineHeight: '1.4 !important',
              cursor: item.locked ? 'not-allowed' : 'inherit',
              opacity: item.locked ? 0.6 : 1,
            }}
          >
            {item.name}
          </Typography>
        </Stack>

        {item.badge !== undefined && (
          <Badge
            badgeContent={item.badge}
            color="secondary"
            showZero
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "#d22268", // Primary pink color
                color: "#fff",
                fontSize: "11px", // Smaller for modern look
                fontWeight: 600,
                minWidth: "18px", // Smaller badge
                height: "18px", // Smaller badge
                marginRight: "8px",
                borderRadius: "9px", // More rounded
              },
            }}
          />
        )}

        {hasChildren && (open ? <ExpandLess sx={{ color: textColor }} /> : <ExpandMore sx={{ color: textColor }} />)}
      </Stack>

      {hasChildren && item.subItems && (
        <Collapse in={open}>
          <Stack>
            {item.subItems.map((subItem: SidebarItemType) => (
              <SidebarItem key={subItem.name} item={subItem} depth={depth + 1} />
            ))}
          </Stack>
        </Collapse>
      )}
    </>
  );
};
